import type { DocumentHandle, DocumentStore } from "@supergrain/silo"
import { createDocumentStoreContext } from "@supergrain/silo/react"

import type { TypeToModel } from "@shared/models/type-to-model"

const {
  Provider: SupergrainProvider,
  /**
   * Supergrain useDocument hook.
   *
   * Must be used within the SupergrainProvider hook provided with a store.
   * For the frontend (hence the shared unified-tiptap), this happens in react-render.tsx.
   */
  useDocument,
  /**
   * Supergrain useDocumentsTogether hook: fetches many documents of one type by id.
   *
   * Like {@link useDocument}, must be used within the SupergrainProvider provided with a store.
   */
  useDocumentsTogether,
  /**
   * Supergrain useDocumentsIndividually hook: like {@link useDocumentsTogether}, but each
   * document settles on its own. Use it for lists that should render what has arrived rather
   * than wait on — or be emptied by — their slowest or missing member.
   */
  useDocumentsIndividually,
  useDocumentStore,
  useQuery,
} = createDocumentStoreContext<DocumentStore<TypeToModel>>()

export {
  SupergrainProvider,
  useDocument,
  useDocumentsIndividually,
  useDocumentsTogether,
  useDocumentStore,
  useQuery,
}

/** A {@link useDocument} handle that has settled on an actual document, attributes and all. */
export function hasResolvedDocumentValue<T extends { attributes: unknown }>(
  handle: DocumentHandle<T>,
): handle is Extract<DocumentHandle<T>, { status: "success" }> {
  return handle.status === "success" && handle.value.attributes !== undefined
}
